import { useMemo, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/motion";
import { CtaBand } from "@/components/CtaBand";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { usePageMeta } from "@/lib/seo";

import { Search, ArrowRight } from "lucide-react";

export const Faq = () => {
    const {  t, tHtml, site: siteData } = useLang();
    const { FAQ_CATEGORIES } = siteData;
    usePageMeta(
        t("meta.faq.title", "International Patient FAQ"),
        t("meta.faq.desc", "Honest answers for international patients — travel and visa, stay, medical care, costs, documents, during your stay, after treatment, and safety with UniversHeal in Kerala.")
    );
    const [query, setQuery] = useState("");
    const [activeCat, setActiveCat] = useState("All");

    const categories = ["All", ...FAQ_CATEGORIES.map((c) => c.title)];

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return FAQ_CATEGORIES.filter((c) => activeCat === "All" || c.title === activeCat)
            .map((c) => ({
                ...c,
                items: c.items.filter(
                    (item) =>
                        !q ||
                        item.q.toLowerCase().includes(q) ||
                        item.a.toLowerCase().includes(q)
                ),
            }))
            .filter((c) => c.items.length > 0);
    }, [query, activeCat]);

    const totalResults = filtered.reduce((n, c) => n + c.items.length, 0);

    return (
        <>
            <PageHero
                eyebrow={t("faq.eyebrow", "International Patient FAQ")}
                title={t("faq.title1", "Questions, answered honestly.")}
                intro={t("faq.intro", "The questions international patients ask us most — searchable and grouped by theme, answered the way we would want them answered.")}
            />

            <section className="section-pad pt-6">
                <div className="container-site max-w-3xl">
                    {/* search */}
                    <Reveal>
                        <div className="relative">
                            <Search className="w-5 h-5 text-moss absolute left-4 top-1/2 -translate-y-1/2" />
                            <input
                                type="search"
                                id="faq-search"
                                data-testid="faq-search"
                                aria-label="Search the FAQ"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search questions — visa, costs, reports…"
                                className="input-field !pl-12 !py-4"
                            />
                        </div>
                    </Reveal>

                    {/* category chips */}
                    <Reveal delay={0.05}>
                        <div
                            className="mt-6 flex gap-2 overflow-x-auto pb-2 -mx-5 px-5 sm:mx-0 sm:px-0 sm:flex-wrap"
                            data-testid="faq-categories"
                            role="tablist"
                            aria-label="FAQ categories"
                        >
                            {categories.map((cat) => (
                                <button
                                    key={cat}
                                    type="button"
                                    role="tab"
                                    aria-selected={activeCat === cat}
                                    data-testid={`faq-category-${cat
                                        .toLowerCase()
                                        .replace(/[^a-z0-9]+/g, "-")}`}
                                    onClick={() => setActiveCat(cat)}
                                    className={`shrink-0 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wide border transition-all duration-200 ${
                                        activeCat === cat
                                            ? "bg-forest text-ivory border-forest"
                                            : "bg-white text-moss border-pine/15 hover:border-pine/40 hover:text-forest"
                                    }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </Reveal>

                    <p className="mt-4 text-xs text-moss" data-testid="faq-result-count">
                        {totalResults} {totalResults === 1 ? "answer" : "answers"}
                        {query.trim() && ` matching "${query.trim()}"`}
                    </p>

                    {/* results */}
                    {filtered.length > 0 ? (
                        <div className="mt-6">
                            {filtered.map((group, gi) => (
                                <div key={group.title} className={gi > 0 ? "mt-10" : ""}>
                                    {activeCat === "All" && (
                                        <Reveal>
                                            <h2 className="eyebrow text-natural mb-4 flex items-center gap-3">
                                                <span className="h-px w-8 bg-natural/50" />
                                                {group.title}
                                            </h2>
                                        </Reveal>
                                    )}
                                    <Accordion type="single" collapsible>
                                        {group.items.map((item, qi) => (
                                            <AccordionItem
                                                key={item.q}
                                                value={`${group.title}-${qi}`}
                                                className="border-pine/15"
                                            >
                                                <AccordionTrigger
                                                    data-testid={`faq-item-${group.title
                                                        .toLowerCase()
                                                        .replace(/[^a-z0-9]+/g, "-")}-${qi}`}
                                                    className="text-left font-serif text-xl sm:text-[1.35rem] text-forest hover:no-underline [&>svg]:text-natural"
                                                >
                                                    {item.q}
                                                </AccordionTrigger>
                                                <AccordionContent
                                                    data-testid={`faq-answer-${group.title
                                                        .toLowerCase()
                                                        .replace(/[^a-z0-9]+/g, "-")}-${qi}`}
                                                    className="text-[15px] leading-relaxed text-moss"
                                                >
                                                    {item.a}
                                                </AccordionContent>
                                            </AccordionItem>
                                        ))}
                                    </Accordion>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div
                            data-testid="faq-empty-state"
                            className="mt-8 bg-white/70 border border-dashed border-pine/30 rounded-2xl px-8 py-14 text-center"
                        >
                            <p className="font-serif italic text-2xl text-forest">
                                {t("faq.text2", "No answers match your search.")}
                            </p>
                            <p className="mt-3 text-sm text-moss">
                                {t("faq.text3", "Try a different word — or ask us directly, a person will reply.")}
                            </p>
                            <a
                                href="/contact"
                                data-testid="faq-empty-contact"
                                className="btn-primary mt-8 inline-flex group"
                            >
                                {t("faq.text4", "Contact us")}
                                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </a>
                        </div>
                    )}

                    <Reveal>
                        <p className="mt-12 text-xs leading-relaxed text-moss">
                            {t("faq.text5", "UniversHeal coordinates access to healthcare services and does not itself diagnose or provide medical treatment.")}
                        </p>
                    </Reveal>
                </div>
            </section>

            <CtaBand />
        </>
    );
};
